import {
  getAzureOpenAIApiKey,
  getAzureOpenAIEndpoint,
} from '@rsos/utils/metaTags';

const DEPLOYMENT = 'gpt-4o-mini';
const API_VERSION = '2024-08-01-preview';

export interface AzureChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AzureChatOptions {
  /** Hard ceiling on round-trip; classifiers should fall back, not block. */
  timeoutMs?: number;
  temperature?: number;
  maxTokens?: number;
  /** Force Azure JSON-mode (no ```json fences). Default true. */
  jsonMode?: boolean;
}

function buildChatCompletionsUrl(): string | null {
  const endpoint = getAzureOpenAIEndpoint();
  if (!endpoint) return null;
  const base = endpoint.trim().replace(/\/+$/, '');
  return `${base}/openai/deployments/${DEPLOYMENT}/chat/completions?api-version=${API_VERSION}`;
}

/**
 * Thin wrapper over Azure OpenAI's chat completions endpoint. Returns the
 * model's reply content, or `null` on any failure mode (missing creds,
 * non-2xx, abort, timeout, malformed body) so callers can fall back to
 * heuristics with a single branch.
 */
export async function callAzureOpenAIChat(
  messages: AzureChatMessage[],
  signal: AbortSignal,
  options: AzureChatOptions = {},
): Promise<string | null> {
  const url = buildChatCompletionsUrl();
  const apiKey = getAzureOpenAIApiKey();
  if (!url || !apiKey) return null;

  const {
    timeoutMs = 4000,
    temperature = 0,
    maxTokens = 60,
    jsonMode = true,
  } = options;

  try {
    const timeout = AbortSignal.timeout(timeoutMs);
    const combined = AbortSignal.any([signal, timeout]);

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'api-key': apiKey,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        temperature,
        max_tokens: maxTokens,
        ...(jsonMode ? { response_format: { type: 'json_object' } } : {}),
      }),
      signal: combined,
    });
    if (!resp.ok) return null;

    const data = await resp.json();
    const content: unknown = data?.choices?.[0]?.message?.content;
    return typeof content === 'string' ? content : null;
  } catch {
    return null;
  }
}
